/**
 * The gym: authored, measured tuning geometry (design report §5). Dimensions are locked to the
 * tuning math so mismatches are visible, not measured — e.g. the gap ladder brackets the derived
 * max jump distance (~4.6 m at full run), and the hover canyon (6.5 m) is only crossable with
 * jump + hover. Pure data + math, no three.js — the headless feel tests load THIS file, so tests
 * and the playable level share one source of truth.
 *
 * Stations 1–11 of 12 (M4). Still to come: rhythm run tuning pass (M5).
 */
export interface GymBox {
  /** Center position, meters. */
  pos: [number, number, number];
  /** Full extents, meters. */
  size: [number, number, number];
  /** Rotation about Z, degrees (ramps). */
  rotZDeg?: number;
  station: string;
  /** Surface tag plumbed through the collision service to the feedback bus (M4 surface grid). */
  surface?: string;
}

/**
 * Moving platform (station 10). Kinematic in physics; its trajectory is THIS pure function of
 * sim time, so headless tests, the physics service, and the renderer all agree exactly.
 */
export interface PlatformDef {
  kind: 'linear' | 'circular' | 'elevator';
  /** Trajectory center (linear: rest point; circular: circle center; elevator: mid-height). */
  center: [number, number, number];
  /** Full extents, meters. */
  size: [number, number, number];
  periodS: number;
  /** linear: ±X half-range; circular: radius (XZ plane); elevator: ±Y half-range. */
  amplitude: number;
  station: string;
}

export const GYM_STATIONS = {
  /*
   * ── ⭐ 1. THE FLOOR — LEVEL CONTENT, NOT ENGINE FURNITURE ────────────────────────────────────
   *
   * Until this entry the gym stood on a 240 x 240 m quad `createScene` built unconditionally and
   * a `cuboid(120, 0.5, 120)` `addGroundPlane()` put under every world — geometry no world
   * authored, that no creator could select, that occupancy voxelised anyway (580 of the pinned
   * Sponza fixture's 3,346 surface voxels, 17.3 %, were the quad) and that stood a metre ABOVE
   * Sponza's own marble once the owner doubled the building. It is a box now, so it is one thing
   * in one place: an instance of the gym's `InstancedMesh`, a Rapier cuboid, a shadow receiver,
   * and — the owner's actual complaint — a member of the editor's pick set.
   *
   * ⚠️ THE TOP FACE IS AT EXACTLY y = 0 AND THE THICKNESS HANGS BELOW IT. Every station below
   * measures its heights from y = 0 (the step ladder's `top / 2`, the surface patches' 0.06 m
   * tops, the derived 4.6 m jump the gap ladder brackets), so a floor whose top were anywhere
   * else would re-tune the whole gym silently.
   *
   * The extent is the course's own measured content AABB — x [-2, 78.1], z [-120.5, -8.5] over
   * `buildGym()` — plus ~18 m of run-off on every side, because the gap ladder, the hover canyon
   * and the step ladder are all authored to be MISSED, landed under, and walked back from. It is
   * NOT 240 m: a mesh over a 120 m bounding radius is invisible to `drawnBounds`
   * (`BOUNDS_SKIP_RADIUS_M`), and a floor GI cannot see is the old quad with a new name.
   */
  floor: { minX: -20, maxX: 85, minZ: -130, maxZ: 10, thicknessM: 0.5 },
  gapLadder: { z: -10, gaps: [1, 2, 3, 4, 4.6, 5, 5.5, 6, 7], top: 1.5 },
  stepLadder: { z: -20, rises: [0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6] },
  slopeFan: { z: -32, angles: [10, 20, 30, 40, 45, 50, 55, 60, 70] },
  heightLadder: { z: -44, heights: [0.5, 1.0, 1.5, 2.0, 2.5, 3.0] },
  hoverCanyon: { z: -54, gap: 6.5, top: 2.0 },
  ledgeRow: { z: -64, top: 2.0 },
  perch: { z: -72, heights: [3.0, 2.5, 2.0, 1.5, 1.0] },
  // Beat-spaced hurdles: at max run (7 m/s), 3.5 m spacing = one jump every 0.5 s (120 BPM).
  // Every 4th interval doubles (a held note — clear it with a full-height jump). If the tuning
  // is right this feels musical; if not, arrhythmic. The best single subjective test.
  rhythmRun: { z: -84, spacing: 3.5, hurdles: 18, hurdleHeight: 0.5 },
  // Camera torture: tight 90° turns, thin pillars, a low ceiling, a narrow slot. Validates
  // pull-in ≤ 1 frame, push-out only after clear frames, and character dither over camera jam.
  cornerAlley: { z: -96, wallHeight: 3 },
  // Rides + velocity inheritance: stand-on tracking, jump-off launch, land-on catch.
  movingPlatforms: { z: -104 },
  // Feedback-bus surface tags: every patch must sound/read differently on step and land.
  surfaceGrid: { z: -112, surfaces: ['metal', 'grass', 'wood', 'sand', 'ice', 'water'] },
  // Surface PHYSICS lane: 25 m of ice — long enough to feel the glide, the drift, and the
  // 3×-stopping-distance golden by hand (the 4 m grid patch is audio-only scale).
  iceRun: { z: -118, length: 25 },
} as const;

// 10. Moving platforms — low tops so all three are boardable from the ground.
export const GYM_PLATFORMS: PlatformDef[] = [
  {
    kind: 'linear',
    center: [0, 0.5, GYM_STATIONS.movingPlatforms.z],
    size: [3, 0.4, 3],
    periodS: 6,
    amplitude: 4,
    station: 'movingPlatforms',
  },
  {
    kind: 'circular',
    center: [12, 0.8, GYM_STATIONS.movingPlatforms.z],
    size: [3, 0.4, 3],
    periodS: 8,
    amplitude: 3,
    station: 'movingPlatforms',
  },
  {
    // Oscillates 0.5 ↔ 3 m (center height): mid 1.75, ±1.25.
    kind: 'elevator',
    center: [22, 1.75, GYM_STATIONS.movingPlatforms.z],
    size: [3, 0.4, 3],
    periodS: 7,
    amplitude: 1.25,
    station: 'movingPlatforms',
  },
];

/** Pure trajectory: platform CENTER at sim time `timeS`. Smooth (sin/cos), deterministic. */
export function platformPosAt(def: PlatformDef, timeS: number): [number, number, number] {
  const phase = (2 * Math.PI * timeS) / def.periodS;
  const [cx, cy, cz] = def.center;
  switch (def.kind) {
    case 'linear':
      return [cx + def.amplitude * Math.sin(phase), cy, cz];
    case 'circular':
      return [cx + def.amplitude * Math.cos(phase), cy, cz + def.amplitude * Math.sin(phase)];
    case 'elevator':
      return [cx, cy + def.amplitude * Math.sin(phase), cz];
  }
}

export function buildGym(): GymBox[] {
  const boxes: GymBox[] = [];

  // 1. The floor — see GYM_STATIONS.floor. Top face at y = 0; the thickness hangs below.
  {
    const { minX, maxX, minZ, maxZ, thicknessM } = GYM_STATIONS.floor;
    boxes.push({
      pos: [(minX + maxX) / 2, -thicknessM / 2, (minZ + maxZ) / 2],
      size: [maxX - minX, thicknessM, maxZ - minZ],
      station: 'ground',
    });
  }

  // 2. Gap ladder — elevated platforms; the wrong gap becoming clearable is the regression view.
  {
    const { z, gaps, top } = GYM_STATIONS.gapLadder;
    const len = 4;
    let startX = 0;
    boxes.push({ pos: [startX + len / 2, top - 0.15, z], size: [len, 0.3, 3], station: 'gapLadder' });
    for (const gap of gaps) {
      startX += len + gap;
      boxes.push({
        pos: [startX + len / 2, top - 0.15, z],
        size: [len, 0.3, 3],
        station: 'gapLadder',
      });
    }
  }

  // 3. Step ladder — rises 0.10→0.60; autostep threshold is 0.40.
  {
    const { z, rises } = GYM_STATIONS.stepLadder;
    let top = 0;
    rises.forEach((rise, i) => {
      top += rise;
      boxes.push({ pos: [i * 1.2 + 0.6, top / 2, z], size: [1.2, top, 3], station: 'stepLadder' });
    });
  }

  // 4. Slope fan — climb boundary 50°, slide boundary 55°.
  {
    const { z, angles } = GYM_STATIONS.slopeFan;
    angles.forEach((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      const lane = i * 5;
      boxes.push({
        pos: [lane + Math.cos(rad) * 4, Math.sin(rad) * 4, z],
        size: [8, 0.3, 3],
        rotZDeg: deg,
        station: 'slopeFan',
      });
    });
  }

  // 5. Height ladder — 2.0 m must be reachable (jumpHeight), 2.5 m must not (without hover).
  {
    const { z, heights } = GYM_STATIONS.heightLadder;
    heights.forEach((h, i) => {
      boxes.push({ pos: [i * 4, h / 2, z], size: [2.5, h, 2.5], station: 'heightLadder' });
    });
  }

  // 6. Hover canyon — jump-impossible, jump+hover-possible. Calibrates hover duration.
  {
    const { z, gap, top } = GYM_STATIONS.hoverCanyon;
    boxes.push({ pos: [3, top - 0.2, z], size: [6, 0.4, 4], station: 'hoverCanyon' });
    // Far platform is long (8 m) so a full-fuel hover lands on it rather than overshooting.
    boxes.push({ pos: [6 + gap + 4, top - 0.2, z], size: [8, 0.4, 4], station: 'hoverCanyon' });
  }

  // 8. Ledge row — approach the drop at walk (teeter) and at run (commit).
  {
    const { z, top } = GYM_STATIONS.ledgeRow;
    boxes.push({ pos: [10, top - 0.2, z], size: [20, 0.4, 6], station: 'ledgeRow' });
  }

  // 7. Precision perch — descending 0.6 m pillars (edge magnetism + framing, M2 partial).
  {
    const { z, heights } = GYM_STATIONS.perch;
    heights.forEach((h, i) => {
      boxes.push({ pos: [i * 2, h / 2, z], size: [0.6, h, 0.6], station: 'perch' });
    });
  }

  // 9. Corner alley — camera collision torture: L-corridor, thin pillars, low ceiling, narrow slot.
  {
    const { z, wallHeight } = GYM_STATIONS.cornerAlley;
    const h = wallHeight;
    const walls: Array<[number, number, number, number, number, number]> = [
      [5, h / 2, z - 1.45, 10, h, 0.4], // north wall, first leg
      [3.5, h / 2, z + 1.45, 7, h, 0.4], // south wall, first leg (ends early → turn)
      [10.2, h / 2, z, 0.4, h, 3.3], // end cap forcing the 90° turn
      [6.8, h / 2, z + 3.5, 0.4, h, 4.5], // west wall, second leg
      [10.2, h / 2, z + 3.7, 0.4, h, 4.1], // east wall, second leg
      [7.6, h / 2, z + 7, 1.6, h, 0.4], // narrow slot, left jamb
      [10.0, h / 2, z + 7, 1.6, h, 0.4], // narrow slot, right jamb (0.8 m gap)
      [8.6, 2.5, z + 4.2, 3, 0.3, 3], // low ceiling patch
    ];
    for (const [x, y, zz, sx, sy, sz] of walls) {
      boxes.push({ pos: [x, y, zz], size: [sx, sy, sz], station: 'cornerAlley' });
    }
    for (const [px, pz] of [
      [8.5, z + 3],
      [8.0, z + 4.5],
      [9.3, z + 5.8],
    ] as const) {
      boxes.push({ pos: [px, h / 2, pz], size: [0.3, h, 0.3], station: 'cornerAlley' });
    }
  }

  // 12. Rhythm run — "tempo and melody".
  {
    const { z, spacing, hurdles, hurdleHeight } = GYM_STATIONS.rhythmRun;
    let x = 0;
    for (let i = 0; i < hurdles; i++) {
      x += i > 0 && i % 4 === 0 ? spacing * 2 : spacing;
      boxes.push({
        pos: [x, hurdleHeight / 2, z],
        size: [0.35, hurdleHeight, 3],
        station: 'rhythmRun',
      });
    }
  }

  // 11. Surface grid — six thin patches in a row; tops at 0.06 m so walking on is seamless.
  {
    const { z, surfaces } = GYM_STATIONS.surfaceGrid;
    surfaces.forEach((surface, i) => {
      boxes.push({ pos: [i * 5, 0, z], size: [4, 0.12, 4], station: 'surfaceGrid', surface });
    });
  }

  // 11b. Ice run — the surface-PHYSICS lane (glide, drift, 3× stopping distance, by hand).
  {
    const { z, length } = GYM_STATIONS.iceRun;
    boxes.push({
      pos: [length / 2, 0, z],
      size: [length, 0.12, 5],
      station: 'surfaceGrid',
      surface: 'ice',
    });
  }

  return boxes;
}
